import { useState } from 'react';
import {
  Color, HR_COMPLAINTS_PATH, HR_COMPLAINTS_ROUTE, Size, complaintActionPath, complaintCloseProblem, pageKindLineText, t,
  type ColumnDef, type ComplaintRow, type ComplaintsAnswer, type PageSize, type SortState, viewerScopeKey,
} from '@bms2/shared';
import { Button, Textarea } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer } from '../../components/composites/ListViewer';
import { useNotice } from '../../components/controls/useNotice';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { PageModule } from '../../shell/PageModule';
import { post, useApi } from '../records/api';
import { line } from '../settings/SettingsShell';
import { HrPagesNav } from './HrPagesNav';
import styles from './HrPages.module.css';

export { HR_COMPLAINTS_PATH, HR_COMPLAINTS_ROUTE };

const COLUMNS: readonly ColumnDef[] = [
  { key: 'raisedAt', label: t('hr.complaint.col.raised'), role: 'date', type: 'instant' },
  { key: 'raisedByName', label: t('hr.complaint.col.raisedBy'), role: 'primary', type: 'text' },
  { key: 'aboutText', label: t('hr.complaint.col.about'), role: 'secondary', type: 'text' },
  { key: 'statusText', label: t('hr.complaint.col.status'), role: 'secondary', type: 'text' },
  { key: 'investigatorText', label: t('hr.complaint.col.investigator'), role: 'secondary', type: 'text' },
];

const STATUS_LABEL = { Open: 'hr.complaint.status.open', UnderInvestigation: 'hr.complaint.status.underInvestigation', Closed: 'hr.complaint.status.closed' } as const;

/**
 * The complaints page (piece 34, Complaints; decision 1468). HR and Admin list
 * every complaint and work it: take the investigation, record the findings
 * and the action, close. A complainant reads their own and works none. 🔒 The
 * route serves only the complaints `canViewHrRecord` reaches, so a manager
 * above the complainant sees nothing here.
 */
export function ComplaintsPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const fetched = useApi<ComplaintsAnswer>(HR_COMPLAINTS_PATH, apiBaseUrl);
  const [noticeBanner, setNotice] = useNotice();
  const [sort, setSort] = useState<SortState | null>({ key: 'raisedAt', direction: 'desc' });
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <PageModule title={t('hr.complaint.title')} subtitle={pageKindLineText({ page: 'named', route: HR_COMPLAINTS_ROUTE })} icon="hr">
      <div className={styles.page} data-page="hr-complaints">
        <HrPagesNav current={HR_COMPLAINTS_ROUTE} />
        {noticeBanner}
        <AsyncStatePanel state={fetched.state} emptyAction={t('hr.complaint.empty')} onRetry={fetched.reload}>
          {(answer) => {
            const open = answer.rows.find((row) => row.id === openId) ?? null;
            return (
              <div data-hr-complaints={String(answer.rows.length)} data-hr-complaints-works={String(answer.works)}>
                <p className={styles.meta}>{answer.works ? t('hr.complaint.hintWorks') : t('hr.complaint.hintMine')}</p>
                <ListViewer
                  viewStore={{ scopeKey: viewerScopeKey('hr-complaints'), apiBaseUrl }}
                  recordType={t('hr.complaint.title')}
                  views={[{ id: 'all', name: t('hr.complaint.title') }]}
                  currentViewId="all"
                  onViewChange={() => undefined}
                  columns={COLUMNS}
                  rows={answer.rows.map((row) => ({ ...row, aboutText: row.aboutName ?? t('hr.complaint.aboutNobody'), statusText: t(STATUS_LABEL[row.status]), investigatorText: row.investigatingName ?? '—' }))}
                  idOf={(row) => row.id}
                  read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
                  sort={sort}
                  onSortChange={setSort}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  emptyAction={t('hr.complaint.empty')}
                  onNavigate={(row) => setOpenId(row.id)}
                />
                {open ? (
                  <ComplaintModal
                    key={`${open.id}:${open.status}`}
                    row={open}
                    works={answer.works}
                    apiBaseUrl={apiBaseUrl}
                    onClose={() => setOpenId(null)}
                    onChanged={(text) => { setNotice({ tone: 'good', text }); fetched.reload(); }}
                  />
                ) : null}
              </div>
            );
          }}
        </AsyncStatePanel>
      </div>
    </PageModule>
  );
}

interface ComplaintModalProps {
  row: ComplaintRow;
  works: boolean;
  apiBaseUrl: string;
  onClose: () => void;
  onChanged: (text: string) => void;
}

/** One complaint: what was said, who took it, and for HR the next step on the lifecycle. Keyed to the complaint and its status, so a reopened window never shows another's draft. */
function ComplaintModal({ row, works, apiBaseUrl, onClose, onChanged }: ComplaintModalProps) {
  const [findings, setFindings] = useState(row.findings ?? '');
  const [actionTaken, setActionTaken] = useState(row.actionTaken ?? '');
  /** 🔒 A refused step reads inside the window, which stays open with what was typed (decision 3063). */
  const [refusal, setRefusal] = useState<string | null>(null);
  const act = async (action: 'take' | 'findings' | 'close', done: string) => {
    setRefusal(null);
    const sent = await post<ComplaintRow>(complaintActionPath(row.id, action), { findings, actionTaken }, apiBaseUrl);
    if (!sent.ok) return setRefusal(sent.message);
    onChanged(done);
    if (action !== 'findings') onClose();
  };
  const closeProblem = complaintCloseProblem({ findings, actionTaken });
  const investigating = works && row.status === 'UnderInvestigation';
  return (
    <Modal
      open
      title={t('hr.complaint.recordTitle')}
      subtitle={`${row.raisedByName} · ${t(STATUS_LABEL[row.status])}`}
      dataModal="complaint"
      onCancel={onClose}
      actions={works && row.status === 'Open'
        ? [{ kind: 'primary', label: t('hr.complaint.take'), onAct: () => void act('take', t('hr.complaint.taken')) }, { kind: 'cancel', label: t('modal.cancel'), onAct: onClose }]
        : investigating
          ? [{ kind: 'primary', label: t('hr.complaint.close'), onAct: () => void act('close', t('hr.complaint.closedNotice')), disabled: closeProblem !== null }, { kind: 'cancel', label: t('modal.cancel'), onAct: onClose }]
          // 🔒 Decision 2140: a complaint HR does not act on here writes nothing, so the window carries one Close (decision 3063).
          : [{ kind: 'close', label: t('hr.complaint.done'), onAct: onClose }]}
    >
      <div className={styles.fields} data-complaint-status={row.status}>
        {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
        <p className={styles.meta}>{row.aboutName ? line(t('hr.complaint.about'), '·', row.aboutName) : t('hr.complaint.aboutNobody')}</p>
        <p className={styles.route} data-complaint-summary="true">{row.summary}</p>
        {row.investigatingName ? <p className={styles.meta}>{line(t('hr.complaint.col.investigator'), '·', row.investigatingName)}</p> : null}
        {investigating ? (
          <>
            <Textarea size={Size.Large} label={t('hr.complaint.findings')} value={findings} onChange={setFindings} rows={4} name="complaintFindings" />
            <Textarea size={Size.Large} label={t('hr.complaint.actionTaken')} value={actionTaken} onChange={setActionTaken} rows={3} name="complaintActionTaken" />
            <span>
              <Button size={Size.Small} color={Color.Inactive} text={[t('hr.complaint.saveFindings')]} actions={[() => void act('findings', t('hr.complaint.findingsSaved'))]} />
            </span>
            {closeProblem && (findings || actionTaken) ? <p className={styles.meta}>{closeProblem}</p> : null}
          </>
        ) : (
          <>
            {row.findings ? <p>{line(t('hr.complaint.findings'), '·', row.findings)}</p> : null}
            {row.actionTaken ? <p>{line(t('hr.complaint.actionTaken'), '·', row.actionTaken)}</p> : null}
          </>
        )}
      </div>
    </Modal>
  );
}
